import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Eye, EyeOff, UserPlus, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

/**
 * Self-service signup.
 *
 * Role + site are required. The backend exposes a single source of truth
 * for what's selectable (/auth/signup-options). On success the user is
 * logged in immediately and routed to their dashboard.
 */
export default function Signup() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: '',
    store: '',
  });
  const [roles, setRoles] = useState([]);
  const [stores, setStores] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Email availability — drives the inline hint under the email field.
  // status: 'idle' | 'checking' | 'available' | 'in_use' | 'invalid'
  const [emailStatus, setEmailStatus] = useState('idle');
  const navigate = useNavigate();
  const { signup } = useAuth();

  // Debounced check: re-runs ~400ms after the user stops typing.
  useEffect(() => {
    const raw = (formData.email || '').trim().toLowerCase();
    if (!raw) {
      setEmailStatus('idle');
      return undefined;
    }
    // Quick client-side shape check so we don't ping the API for `a`.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) {
      setEmailStatus('invalid');
      return undefined;
    }
    setEmailStatus('checking');
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_URL}/auth/check-email?email=${encodeURIComponent(raw)}`,
          { signal: ctrl.signal }
        );
        if (!res.ok) return;
        const data = await res.json();
        // If the user kept typing while we were in flight, ignore.
        if (raw !== (formData.email || '').trim().toLowerCase()) return;
        if (data.available) setEmailStatus('available');
        else setEmailStatus(data.reason === 'invalid' ? 'invalid' : 'in_use');
      } catch (err) {
        if (err.name !== 'AbortError') setEmailStatus('idle');
      }
    }, 400);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [formData.email]);

  // Load role + store options from the backend.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/signup-options`);
        if (!res.ok) throw new Error('Could not load signup options');
        const data = await res.json();
        setRoles(data.roles || []);
        setStores(data.stores || []);
      } catch (e) {
        toast.error('Could not load roles/sites. Please reload the page.');
      } finally {
        setOptionsLoading(false);
      }
    })();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.role) {
      toast.error('Please select your role');
      return;
    }
    if (!formData.store) {
      toast.error('Please select the site / store where you work');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (emailStatus === 'in_use') {
      toast.error('This email is already registered. Sign in or use a different email.');
      return;
    }
    if (emailStatus === 'invalid') {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const response = await signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || undefined,
        role: formData.role,
        store: formData.store,
      });

      if (response?.pending_approval) {
        toast.success("Thanks! Your account is awaiting approval.", { duration: 6000 });
        navigate('/login', {
          state: {
            pendingApproval: true,
            message: response.message || "You'll receive an email as soon as your account is approved.",
            email: response.user?.email,
          },
        });
        return;
      }

      // Legacy path (token-bearing response)
      toast.success(`Welcome to PIE, ${response.user.name}!`);
      navigate('/');
    } catch (error) {
      toast.error(error.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-hero">
        <div className="relative z-10 p-12 text-white max-w-lg">
          <img src="/pie-logo.png" alt="PIE — Punjab Imports Ecosystem" className="h-20 mb-8" />
          <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Inter' }}>
            Join Punjab Imports
          </h1>
          <p className="text-lg text-white/80">
            Create your account to start tracking tasks and submitting daily updates.
          </p>
        </div>
      </div>

      <div className="login-form-container">
        <div className="login-form">
          <div className="lg:hidden text-center mb-8">
            <img src="/pie-logo.png" alt="PIE" className="h-16 mx-auto mb-4" />
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Inter' }}>
                Create Account
              </h2>
              <p className="text-slate-600 mt-1">Fill in your details to get started</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  data-testid="signup-name-input"
                  className="focus-visible:ring-[#DC2626]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  data-testid="signup-email-input"
                  className={`focus-visible:ring-[#DC2626] ${
                    emailStatus === 'in_use' ? 'border-red-400 focus-visible:ring-red-400' :
                    emailStatus === 'available' ? 'border-emerald-400 focus-visible:ring-emerald-400' :
                    ''
                  }`}
                />
                {emailStatus === 'checking' && (
                  <p className="text-xs text-slate-400 flex items-center gap-1" data-testid="email-status-checking">
                    <Loader2 size={12} className="animate-spin" /> Checking availability…
                  </p>
                )}
                {emailStatus === 'in_use' && (
                  <p className="text-xs text-red-600 flex items-center gap-1" data-testid="email-status-in-use">
                    <AlertCircle size={12} /> This email is already registered.{' '}
                    <Link to="/login" className="underline hover:text-red-700">Sign in instead?</Link>
                  </p>
                )}
                {emailStatus === 'invalid' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1" data-testid="email-status-invalid">
                    <AlertCircle size={12} /> Enter a valid email address.
                  </p>
                )}
                {emailStatus === 'available' && (
                  <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="email-status-available">
                    <CheckCircle2 size={12} /> Looks good — this email is available.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+61 400 000 000"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="signup-phone-input"
                  className="focus-visible:ring-[#DC2626]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Your Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={optionsLoading}
                >
                  <SelectTrigger className="focus:ring-[#DC2626]" data-testid="signup-role-select">
                    <SelectValue placeholder={optionsLoading ? 'Loading roles...' : 'Select your role'} />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.value} value={r.value} data-testid={`signup-role-option-${r.value}`}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Higher-level roles (CEO, HR, Tech Support, etc.) can only be created by invitation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="store">Site / Store *</Label>
                <Select
                  value={formData.store}
                  onValueChange={(value) => setFormData({ ...formData, store: value })}
                  disabled={optionsLoading}
                >
                  <SelectTrigger className="focus:ring-[#DC2626]" data-testid="signup-store-select">
                    <SelectValue placeholder={optionsLoading ? 'Loading sites...' : 'Select your site'} />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`signup-store-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Inspections, tasks and notifications are routed by site, so pick the right one.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    data-testid="signup-password-input"
                    className="pr-10 focus-visible:ring-[#DC2626]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  data-testid="signup-confirm-password-input"
                  className="focus-visible:ring-[#DC2626]"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                disabled={loading || optionsLoading}
                data-testid="signup-submit-button"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <UserPlus className="mr-2 h-4 w-4" />
                )}
                Create Account
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#DC2626] hover:text-[#B91C1C] font-medium" data-testid="login-link">
                Sign in
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
